import { Button } from 'doctornow-design-system';

import StickyButton from '@/components/atom/buttons/StickyButton';
import ClickLogger from '@/components/common/ClickLogger';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import useTelemedicineRequestCompleteViewModel from '../hooks/useTelemedicineRequestCompleteViewModel';

const DEFAULT_DOCTOR_IMAGE = '/images/doctor-placeholder.png';

const TelemedicineRequestCompleteView = () => {
  const viewModel = useTelemedicineRequestCompleteViewModel();

  if (viewModel.isLoading) {
    return null;
  }

  return (
    <>
      <div className='relative flex min-h-screen flex-col bg-white'>
        <PageTitle doctorName={viewModel.doctorName} />

        <div className='flex flex-1 flex-col items-center justify-center gap-[32px] px-[24px] pt-[218px] pb-[114px]'>
          <DoctorImage doctorImage={viewModel.doctorImage || DEFAULT_DOCTOR_IMAGE} />

          <AppointmentInfoCard
            diagnosisDate={viewModel.diagnosisDate}
            medicalCategoryName={viewModel.medicalCategoryName}
            doctorName={viewModel.doctorName}
          />
        </div>

        <ClickLogger
          params={{
            eventName: EWebTreatmentRequestLog.ClickCtaButton,
            payload: { doctorId: viewModel.doctorId || '' },
          }}
        >
          <FloatingButton onClick={viewModel.handleClickAppButton} onHomeClick={viewModel.onHomeClick} />
        </ClickLogger>
      </div>
    </>
  );
};

interface PageTitleProps {
  doctorName: string;
}

const PageTitle = ({ doctorName }: PageTitleProps) => {
  return (
    <div className='absolute top-[60px] right-0 left-0 flex flex-col gap-[8px] px-[24px] pt-[12px] pb-0'>
      <div className='typography-ui-24sb w-full text-gray-900'>
        <p className='mb-0'>{doctorName} 선생님이</p>
        <p>신청서를 확인하고 있어요</p>
      </div>
      <p className='typography-ui-17r w-full text-gray-600'>진료가 확정되면 카톡으로 알려드릴게요.</p>
    </div>
  );
};

interface DoctorImageProps {
  doctorImage: string;
}

const DoctorImage = ({ doctorImage }: DoctorImageProps) => {
  return (
    <div className='relative flex w-full flex-col items-center justify-center overflow-hidden pt-0 pb-[40px]'>
      <div className='relative mb-[-40px] size-[200px] shrink-0'>
        <img src={doctorImage} alt='의사 이미지' className='pointer-events-none size-full object-cover' />
      </div>
      <div className='mb-[-40px] h-[40px] w-full bg-gradient-to-b from-transparent to-white' />
    </div>
  );
};

interface AppointmentInfoCardProps {
  diagnosisDate: string;
  medicalCategoryName: string;
  doctorName: string;
}

const AppointmentInfoCard = ({ diagnosisDate, medicalCategoryName, doctorName }: AppointmentInfoCardProps) => {
  return (
    <div className='flex w-full flex-col gap-[12px] rounded-[12px] bg-gray-20 p-[20px]'>
      <InfoRow label='예상 진료 시간' value={diagnosisDate} isHighlight />
      <Divider />
      <InfoRow label='증상/과목' value={medicalCategoryName} />
      <Divider />
      <InfoRow label='의사명' value={`${doctorName} 의사`} />
    </div>
  );
};

interface InfoRowProps {
  label: string;
  value: string;
  isHighlight?: boolean;
}

const InfoRow = ({ label, value, isHighlight = false }: InfoRowProps) => {
  return (
    <div className='flex w-full items-center gap-[16px]'>
      <p className='typography-ui-15r shrink-0 whitespace-pre text-gray-400'>{label}</p>
      <p className={`typography-ui-15m grow basis-0 text-right ${isHighlight ? 'text-primary-700' : 'text-gray-700'}`}>
        {value}
      </p>
    </div>
  );
};

const Divider = () => {
  return <div className='h-[1px] w-full shrink-0 bg-gray-40' />;
};

interface FloatingButtonProps {
  onClick: () => void;
  onHomeClick: () => void;
}

const FloatingButton = ({ onClick, onHomeClick }: FloatingButtonProps) => {
  return (
    <StickyButton>
      <div className='flex w-full flex-col gap-[12px]'>
        <ClickLogger
          params={{
            eventName: EWebTreatmentRequestLog.ClickCompleteButton,
          }}
        >
          <Button size='xl' buttonColor='primary' buttonStyle='contained' onClick={onClick} className='w-full'>
            앱에서 진료 현황보기
          </Button>
        </ClickLogger>
        <ClickLogger
          params={{
            eventName: EWebTreatmentRequestLog.ClickCompleteButton,
          }}
        >
          <Button size='xl' buttonColor='gray' buttonStyle='outlined' onClick={onHomeClick} className='w-full'>
            홈으로 돌아가기
          </Button>
        </ClickLogger>
      </div>
    </StickyButton>
  );
};

export default TelemedicineRequestCompleteView;
